import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/ui/select'
import { Textarea } from '@repo/ui/components/ui/textarea'
import { CalendarClock, Loader2, UserPlus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  formatCalendarDateTime,
  getCalendarStateLabel,
  getPrimaryExternalAttendee,
} from '@/lib/calendar/calendar-status'
import type { ImportedCalendarEvent } from '@/lib/calendar/types'
import { trpc } from '@/lib/trpc'

interface ImportedEventPanelProps {
  event: ImportedCalendarEvent
  onConverted?: () => void
}

export function ImportedEventPanel({ event, onConverted }: ImportedEventPanelProps) {
  const utils = trpc.useUtils()
  const { data: services = [] } = trpc.services.list.useQuery()
  const attendee = useMemo(() => getPrimaryExternalAttendee(event), [event])
  const [serviceId, setServiceId] = useState('')
  const [customerName, setCustomerName] = useState(attendee?.name ?? '')
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerEmail, setCustomerEmail] = useState(attendee?.email ?? '')
  const [addressText, setAddressText] = useState(event.locationText ?? '')
  const convertMutation = trpc.calendar.events.convertToAppointment.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.calendar.events.list.invalidate(),
        utils.appointments.list.invalidate(),
      ])
      toast.success('Imported event converted')
      onConverted?.()
    },
    onError: (error) => toast.error('Could not convert event', { description: error.message }),
  })
  const ignoreMutation = trpc.calendar.events.ignore.useMutation({
    onSuccess: async () => {
      await utils.calendar.events.list.invalidate()
      toast.success('Imported event ignored')
      onConverted?.()
    },
    onError: (error) => toast.error('Could not ignore event', { description: error.message }),
  })
  const canSubmit = serviceId && customerName.trim() && customerPhone.trim()

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto bg-background/35 p-4 md:p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Imported calendar event
          </div>
          <h3 className="mt-3 text-xl font-semibold tracking-tight text-foreground">
            {event.title}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatCalendarDateTime(event.startsAt)} - {formatCalendarDateTime(event.endsAt)}
          </p>
        </div>
        <Badge variant="outline" className="rounded-[4px]">
          {getCalendarStateLabel(event)}
        </Badge>
      </div>

      <div className="rounded-[6px] border border-border/70 bg-card/40 p-3 text-sm text-muted-foreground">
        <div className="flex gap-2">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <p className="font-medium text-foreground">
              {event.busy ? 'Blocks booking availability' : 'Free event'}
            </p>
            <p className="mt-1">
              Add service and customer details to manage this event as a Zippity appointment.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-3">
        <div className="grid gap-1.5">
          <Label>Service</Label>
          <Select value={serviceId} onValueChange={setServiceId}>
            <SelectTrigger className="w-full rounded-[4px]">
              <SelectValue placeholder="Choose a service" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="calendar-customer-name">Customer name</Label>
            <Input
              id="calendar-customer-name"
              value={customerName}
              onChange={(eventInput) => setCustomerName(eventInput.target.value)}
              placeholder="Customer name"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="calendar-customer-phone">Phone</Label>
            <Input
              id="calendar-customer-phone"
              value={customerPhone}
              onChange={(eventInput) => setCustomerPhone(eventInput.target.value)}
              placeholder="+1..."
            />
          </div>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="calendar-customer-email">Email</Label>
          <Input
            id="calendar-customer-email"
            value={customerEmail}
            onChange={(eventInput) => setCustomerEmail(eventInput.target.value)}
            placeholder="name@example.com"
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="calendar-address">Address</Label>
          <Textarea
            id="calendar-address"
            value={addressText}
            onChange={(eventInput) => setAddressText(eventInput.target.value)}
            placeholder="Imported event location"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={!canSubmit || convertMutation.isPending}
          onClick={() =>
            convertMutation.mutate({
              eventId: event.id,
              serviceId,
              customerName,
              customerPhone,
              customerEmail,
              addressText,
            })
          }
        >
          {convertMutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <UserPlus className="h-3.5 w-3.5" />
          )}
          Convert
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={ignoreMutation.isPending}
          onClick={() => ignoreMutation.mutate({ eventId: event.id })}
        >
          Ignore
        </Button>
      </div>
    </div>
  )
}
